import React, { useEffect, useState } from "react";
import {
  CompanyUsersAPI,
  ProjectAPI,
  AuthAPI,
} from "./api/apiClient";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useUI } from "./context/UIContext";

type CompanyUser = {
  userNo?: number;
  userName?: string;
  email: string;
  userType?: string | null;
};

type Project = {
  projectNo: number;
  projectName: string;
  projectDescription?: string;
  companyNo?: number;
  companyName?: string;
};

type ProjectUser = {
  userNo?: number;
  userName?: string;
  email: string;
};

type LocationState = {
  companyNo?: number;
  companyName?: string;
};

export default function CompanyAddUser() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    setSelectedProjectId,
    setSelectedProjectName,
  } = useUI();

  /*
   * ---------------------------------------------------------
   * SELECTED COMPANY
   * ---------------------------------------------------------
   *
   * SuperAdminCompanies sends:
   *
   * navigate("/add-user", {
   *   state: {
   *     companyNo,
   *     companyName
   *   }
   * });
   *
   */

  const locationState = location.state as LocationState | null;

  const selectedCompanyNo = Number(
    locationState?.companyNo || 0
  );

  const [companyName, setCompanyName] = useState(
    locationState?.companyName || ""
  );

  /*
   * ---------------------------------------------------------
   * STATE
   * ---------------------------------------------------------
   */

  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>(
    []
  );

  const [availableUsers, setAvailableUsers] = useState<string[]>(
    []
  );

  const [projects, setProjects] = useState<Project[]>([]);

  const [selectedEmail, setSelectedEmail] = useState("");

  const [selectedRole, setSelectedRole] =
    useState("user");

  const [updateEmail, setUpdateEmail] =
    useState("");

  const [updateRole, setUpdateRole] =
    useState("user");

  const [assignProject, setAssignProject] =
    useState<Project | null>(null);

  const [assignUserEmail, setAssignUserEmail] =
    useState("");

  const [selectedProject, setSelectedProject] =
    useState<Project | null>(null);

  const [projectUsers, setProjectUsers] =
    useState<ProjectUser[]>([]);

  const [message, setMessage] =
    useState("");

  const [error, setError] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  /*
   * ---------------------------------------------------------
   * LOAD COMPANY NAME
   * ---------------------------------------------------------
   *
   * If companyName was already passed from the
   * SuperAdminCompanies page, we use it.
   *
   * Otherwise we get it from AuthAPI.me().
   *
   */

  const loadCompanyName = async () => {
    if (!selectedCompanyNo) {
      return;
    }

    if (locationState?.companyName) {
      setCompanyName(locationState.companyName);
      return;
    }

    try {
      const res: any = await AuthAPI.me();

      const companies =
        res.data?.companies || [];

      const selectedCompany = companies.find(
        (company: any) =>
          Number(company.companyNo) ===
          selectedCompanyNo
      );

      if (selectedCompany) {
        setCompanyName(
          selectedCompany.companyName || ""
        );
      }
    } catch (err) {
      console.error(
        "Failed to load company information",
        err
      );
    }
  };

  /*
   * ---------------------------------------------------------
   * LOAD COMPANY DATA
   * ---------------------------------------------------------
   */

  const loadData = async () => {
    if (!selectedCompanyNo) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      /*
       * Load projects belonging to this company.
       */
      try {
        const companyProjects =
          await CompanyUsersAPI.getCompanyProjects(
            selectedCompanyNo
          );

        setProjects(
          Array.isArray(companyProjects)
            ? companyProjects
            : []
        );
      } catch (err) {
        console.error(
          "Projects error:",
          err
        );

        setProjects([]);
      }

      /*
       * Load company users and users who
       * can be added to this company.
       */
      try {
        const users =
          await CompanyUsersAPI.getCompanyUsers(
            selectedCompanyNo
          );

        const available =
          await CompanyUsersAPI.getAvailableUsers(
            selectedCompanyNo
          );

        setCompanyUsers(
          Array.isArray(users)
            ? users
            : []
        );

        setAvailableUsers(
          Array.isArray(available)
            ? available
            : []
        );
      } catch (err) {
        console.error(
          "Company users error:",
          err
        );

        setCompanyUsers([]);
        setAvailableUsers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * INITIAL LOAD
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (!selectedCompanyNo) {
      return;
    }

    loadCompanyName();
    loadData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyNo]);

  /*
   * ---------------------------------------------------------
   * ADD USER
   * ---------------------------------------------------------
   */

  const handleAddUser = async () => {
    setError("");
    setMessage("");

    if (!selectedCompanyNo) {
      setError("No company selected.");
      return;
    }

    if (!selectedEmail.trim()) {
      setError("Enter user email.");
      return;
    }

    try {
      const res: any =
        await CompanyUsersAPI.addUser({
          companyNo: selectedCompanyNo,
          email: selectedEmail.trim(),
          role: selectedRole,
        });

      setMessage(
        res?.message ||
          "User added successfully."
      );

      setSelectedEmail("");

      await loadData();
    } catch (err: any) {
      console.error(
        "Failed to add user:",
        err
      );

      setError(
        err?.message ||
          "Failed to add user."
      );
    }
  };

  /*
   * ---------------------------------------------------------
   * LOAD USER ROLE
   * ---------------------------------------------------------
   */

  const loadUserRole = async (
    email: string
  ) => {
    if (!email || !selectedCompanyNo) {
      return;
    }

    try {
      const res: any =
        await CompanyUsersAPI.getUserRole(
          email,
          selectedCompanyNo
        );

      setUpdateRole(
        res?.role || "user"
      );
    } catch (err: any) {
      console.error(
        "Failed to load user role:",
        err
      );

      setError(
        err?.message ||
          "Unable to load user role."
      );
    }
  };

  /*
   * ---------------------------------------------------------
   * UPDATE USER ROLE
   * ---------------------------------------------------------
   */

  const handleUpdateRole = async () => {
    setError("");
    setMessage("");

    if (!selectedCompanyNo) {
      setError("No company selected.");
      return;
    }

    if (!updateEmail) {
      setError("Select a user.");
      return;
    }

    const currentUser =
      companyUsers.find(
        (u) =>
          u.email?.toLowerCase() ===
          updateEmail.toLowerCase()
      );

    /*
     * Don't call API if role is already the same.
     */
    if (
      currentUser &&
      currentUser.userType
        ?.toLowerCase() ===
        updateRole.toLowerCase()
    ) {
      setMessage(
        `User already has the '${updateRole}' role.`
      );
      return;
    }

    try {
      const res: any =
        await CompanyUsersAPI.updateUserRole({
          companyNo: selectedCompanyNo,
          email: updateEmail,
          role: updateRole,
        });

      setMessage(
        res?.message ||
          "User role changed successfully."
      );

      await loadData();

      setUpdateEmail("");
      setUpdateRole("user");
    } catch (err: any) {
      console.error(
        "Failed to update role:",
        err
      );

      setError(
        err?.message ||
          "Failed to update role."
      );
    }
  };

  /*
   * ---------------------------------------------------------
   * REMOVE USER FROM COMPANY
   * ---------------------------------------------------------
   */

  const handleRemoveUser = async (
    email: string
  ) => {
    setError("");
    setMessage("");

    if (!selectedCompanyNo) {
      setError("No company selected.");
      return;
    }

    try {
      const res: any =
        await CompanyUsersAPI.removeUser({
          companyNo: selectedCompanyNo,
          email,
        });

      setMessage(
        res?.message ||
          "User removed successfully."
      );

      await loadData();
    } catch (err: any) {
      console.error(
        "Failed to remove user:",
        err
      );

      setError(
        err?.message ||
          "Failed to remove user."
      );
    }
  };

  /*
   * ---------------------------------------------------------
   * VIEW PROJECT USERS
   * ---------------------------------------------------------
   */

  const handleProjectClick = async (
    project: Project
  ) => {
    setSelectedProject(project);

    try {
      const users =
        await ProjectAPI.getUsers(
          project.projectNo
        );

      setProjectUsers(
        Array.isArray(users)
          ? users
          : []
      );
    } catch (err) {
      console.error(
        "Failed to load project users:",
        err
      );

      setProjectUsers([]);
    }
  };

  /*
   * ---------------------------------------------------------
   * ASSIGN USER TO PROJECT
   * ---------------------------------------------------------
   */

  const handleAssignUserToProject =
    async () => {
      setError("");
      setMessage("");

      if (!assignProject) {
        setError("Select a project.");
        return;
      }

      if (!assignUserEmail) {
        setError("Select a user.");
        return;
      }

      try {
        await CompanyUsersAPI.assignUserToProject(
          {
            projectNo:
              assignProject.projectNo,
            email: assignUserEmail,
          }
        );

        setMessage(
          "User assigned successfully."
        );

        setAssignUserEmail("");

        /*
         * Refresh project users immediately.
         */
        await handleProjectClick(
          assignProject
        );

        setAssignProject(null);
      } catch (err: any) {
        console.error(
          "Failed to assign user:",
          err
        );

        setError(
          err?.message ||
            "Failed to assign user."
        );
      }
    };

  /*
   * ---------------------------------------------------------
   * REMOVE USER FROM PROJECT
   * ---------------------------------------------------------
   */

  const handleRemoveUserFromProject =
    async (
      project: Project,
      email: string
    ) => {
      setError("");
      setMessage("");

      try {
        await CompanyUsersAPI.removeUserFromProject(
          {
            projectNo:
              project.projectNo,
            email,
          }
        );

        setMessage(
          "User removed from project."
        );

        await handleProjectClick(
          project
        );
      } catch (err: any) {
        console.error(
          "Failed to remove user from project:",
          err
        );

        setError(
          err?.message ||
            "Failed to remove user from project."
        );
      }
    };

  /*
   * ---------------------------------------------------------
   * OPEN PROJECTS PAGE
   * ---------------------------------------------------------
   */

  const handleOpenProjectsPage = () => {
    if (projects.length === 0) {
      setError("No projects available.");
      return;
    }

    const project = projects[0];

    setSelectedProjectId(
      project.projectNo
    );

    setSelectedProjectName(
      project.projectName
    );

    navigate("/");
  };

  /*
   * ---------------------------------------------------------
   * NO COMPANY SELECTED
   * ---------------------------------------------------------
   */

  if (!selectedCompanyNo) {
    return (
      <div className="h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-bold mb-3">
            No Company Selected
          </h1>

          <p className="text-gray-600 mb-6">
            Please select a company from the
            Super Admin company list.
          </p>

          <button
            onClick={() =>
              navigate("/super-admin")
            }
            className="bg-blue-600 text-white px-5 py-2 rounded"
          >
            Select Company
          </button>
        </div>
      </div>
    );
  }

  /*
   * ---------------------------------------------------------
   * MAIN UI
   * ---------------------------------------------------------
   */

  return (
    <div className="h-screen overflow-y-auto bg-gray-100 p-6">

      {/* =====================================================
          HEADER
      ====================================================== */}

      <div className="bg-white rounded-xl shadow p-5 mb-6">

        <div className="flex items-center">

          <button
            onClick={() =>
              navigate("/super-admin")
            }
            className="mr-4 text-blue-600 font-semibold text-2xl"
            title="Back to companies"
          >
            ←
          </button>

          <div className="flex-1">
            <h1 className="text-2xl font-bold">
              Company SuperAdmin Dashboard
            </h1>

            <div className="mt-2 text-sm text-gray-600">
              <span className="font-semibold">
                Company:
              </span>{" "}
              {companyName ||
                "Selected Company"}
            </div>

            <div className="text-sm text-gray-500">
              <span className="font-semibold">
                Company No:
              </span>{" "}
              {selectedCompanyNo}
            </div>
          </div>

          <button
            onClick={() =>
              navigate("/super-admin")
            }
            className="hidden sm:block border border-gray-300 px-4 py-2 rounded text-sm"
          >
            Change Company
          </button>

        </div>

        {loading && (
          <p className="text-blue-600 text-sm mt-4">
            Loading company data...
          </p>
        )}

        {message && (
          <p className="text-green-600 mt-4">
            {message}
          </p>
        )}

        {error && (
          <p className="text-red-500 mt-4">
            {error}
          </p>
        )}

      </div>

      {/* =====================================================
          COMPANY USERS
      ====================================================== */}

      <div className="bg-white p-6 rounded-xl shadow mb-6">

        <h2 className="text-lg font-semibold mb-3">
          Company Users
        </h2>

        {companyUsers.length === 0 ? (
          <p className="text-gray-500">
            No users found.
          </p>
        ) : (
          companyUsers.map(
            (user, index) => (
              <div
                key={
                  user.userNo ??
                  `${user.email}-${index}`
                }
                className="flex flex-col sm:flex-row sm:justify-between sm:items-center border p-3 mb-2 rounded gap-2"
              >

                <div className="break-all">
                  <div className="font-medium">
                    {user.userName ||
                      user.email}
                  </div>

                  <div className="text-sm text-gray-500">
                    {user.email}
                  </div>

                  <div className="text-xs text-blue-600 mt-1">
                    Role:{" "}
                    {user.userType ||
                      "Not Assigned"}
                  </div>
                </div>

                <button
                  onClick={() =>
                    handleRemoveUser(
                      user.email
                    )
                  }
                  className="text-red-500 text-sm sm:text-base"
                >
                  Remove
                </button>

              </div>
            )
          )
        )}

      </div>

      {/* =====================================================
          ADD USER
      ====================================================== */}

      <div className="bg-white p-6 rounded-xl shadow mb-6">

        <h2 className="text-lg font-semibold mb-3">
          Add User
        </h2>

        <input
          type="email"
          value={selectedEmail}
          onChange={(e) =>
            setSelectedEmail(
              e.target.value
            )
          }
          placeholder="Enter user email"
          className="w-full border p-2 rounded mb-3"
        />

        {selectedEmail &&
          availableUsers.some(
            (email) =>
              email.toLowerCase() ===
              selectedEmail
                .toLowerCase()
          ) && (
            <select
              value={selectedEmail}
              onChange={(e) =>
                setSelectedEmail(
                  e.target.value
                )
              }
              className="w-full border p-2 rounded mb-3"
            >
              {availableUsers
                .filter(
                  (email) =>
                    email
                      .toLowerCase() ===
                    selectedEmail
                      .toLowerCase()
                )
                .map(
                  (
                    email,
                    index
                  ) => (
                    <option
                      key={index}
                      value={email}
                    >
                      {email}
                    </option>
                  )
                )}
            </select>
          )}

        <select
          value={selectedRole}
          onChange={(e) =>
            setSelectedRole(
              e.target.value
            )
          }
          className="w-full border p-2 rounded mb-3"
        >
          <option value="superadmin">
            SuperAdmin
          </option>

          <option value="admin">
            Admin
          </option>

          <option value="user">
            Normal User
          </option>

          <option value="client">
            Client
          </option>

          <option value="external">
            External
          </option>
        </select>

        <button
          onClick={handleAddUser}
          className="w-full bg-green-600 text-white py-2 rounded"
        >
          Add User
        </button>

      </div>

      {/* =====================================================
          UPDATE USER ROLE
      ====================================================== */}

      <div className="bg-white p-6 rounded-xl shadow mb-6">

        <h2 className="text-lg font-semibold mb-3">
          Update User Role
        </h2>

        <select
          value={updateEmail}
          onChange={async (e) => {
            const email =
              e.target.value;

            setUpdateEmail(email);
            setError("");

            if (email) {
              await loadUserRole(
                email
              );
            }
          }}
          className="w-full border p-2 rounded mb-3"
        >
          <option value="">
            Select User
          </option>

          {companyUsers.map(
            (user, index) => (
              <option
                key={
                  user.userNo ??
                  `${user.email}-${index}`
                }
                value={user.email}
              >
                {user.email}
              </option>
            )
          )}
        </select>

        <select
          value={updateRole}
          onChange={(e) =>
            setUpdateRole(
              e.target.value
            )
          }
          className="w-full border p-2 rounded mb-3"
        >
          <option value="superadmin">
            SuperAdmin
          </option>

          <option value="admin">
            Admin
          </option>

          <option value="user">
            User
          </option>

          <option value="client">
            Client
          </option>

          <option value="external">
            External
          </option>
        </select>

        <button
          onClick={handleUpdateRole}
          className="w-full bg-blue-600 text-white py-2 rounded"
        >
          Update Role
        </button>

      </div>

      {/* =====================================================
          COMPANY PROJECTS
      ====================================================== */}

      <div className="bg-white p-6 rounded-xl shadow">

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">

          <div>
            <h2 className="text-lg font-semibold">
              Company Projects
            </h2>

            <p className="text-sm text-gray-500">
              Projects belonging to this company
            </p>
          </div>

          <button
            onClick={
              handleOpenProjectsPage
            }
            className="bg-green-600 text-white px-4 py-2 rounded text-sm"
          >
            Open Projects Page
          </button>

        </div>

        {projects.length === 0 ? (
          <p className="text-gray-500">
            No projects assigned.
          </p>
        ) : (
          projects.map(
            (project) => (
              <div
                key={project.projectNo}
                className="border p-4 mb-3 rounded bg-white shadow-sm"
              >

                {/* =========================================
                    PROJECT HEADER
                ========================================== */}

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">

                  <div>
                    <div className="font-medium">
                      {project.projectName}
                    </div>

                    <div className="text-xs text-gray-500 mt-1">
                      Project No:{" "}
                      {project.projectNo}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">

                    <button
                      onClick={() => {
                        setAssignProject(
                          null
                        );

                        handleProjectClick(
                          project
                        );
                      }}
                      className="text-blue-600 underline text-sm"
                    >
                      See Users
                    </button>

                    <button
                      onClick={async () => {
                        setSelectedProject(
                          null
                        );

                        setAssignProject(
                          project
                        );

                        try {
                          const users =
                            await ProjectAPI.getUsers(
                              project.projectNo
                            );

                          setProjectUsers(
                            Array.isArray(
                              users
                            )
                              ? users
                              : []
                          );
                        } catch (err) {
                          console.error(
                            "Failed to load project users:",
                            err
                          );

                          setProjectUsers(
                            []
                          );
                        }
                      }}
                      className="text-purple-600 underline text-sm"
                    >
                      Assign User
                    </button>

                  </div>

                </div>

                {/* =========================================
                    PROJECT USERS
                ========================================== */}

                {selectedProject?.projectNo ===
                  project.projectNo && (
                  <div className="mt-4 border-t pt-3">

                    <div className="flex justify-between items-center mb-3">

                      <h3 className="font-semibold">
                        Users
                      </h3>

                      <button
                        onClick={() =>
                          setSelectedProject(
                            null
                          )
                        }
                        className="text-gray-500 text-sm"
                      >
                        Close
                      </button>

                    </div>

                    {projectUsers.length ===
                    0 ? (
                      <p className="text-sm text-gray-500">
                        No users assigned to
                        this project.
                      </p>
                    ) : (
                      projectUsers.map(
                        (
                          user,
                          index
                        ) => (
                          <div
                            key={
                              user.userNo ??
                              `${user.email}-${index}`
                            }
                            className="flex flex-col sm:flex-row sm:justify-between sm:items-center border p-2 mb-2 rounded gap-2"
                          >

                            <div className="break-all">

                              <div className="font-medium">
                                {user.userName ||
                                  user.email}
                              </div>

                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>

                            </div>

                            <button
                              onClick={() =>
                                handleRemoveUserFromProject(
                                  project,
                                  user.email
                                )
                              }
                              className="text-red-500 font-semibold text-sm"
                            >
                              Remove
                            </button>

                          </div>
                        )
                      )
                    )}

                  </div>
                )}

                {/* =========================================
                    ASSIGN USER
                ========================================== */}

                {assignProject?.projectNo ===
                  project.projectNo && (
                  <div className="mt-4 border-t pt-3">

                    <div className="flex justify-between items-center mb-3">

                      <h3 className="font-semibold">
                        Assign User
                      </h3>

                      <button
                        onClick={() => {
                          setAssignProject(
                            null
                          );
                          setAssignUserEmail(
                            ""
                          );
                        }}
                        className="text-gray-500 text-sm"
                      >
                        Close
                      </button>

                    </div>

                    <select
                      value={assignUserEmail}
                      onChange={(e) =>
                        setAssignUserEmail(
                          e.target.value
                        )
                      }
                      className="w-full border p-2 rounded mb-2"
                    >
                      <option value="">
                        Select User
                      </option>

                      {companyUsers
                        /*
                         * Only normal users should
                         * appear in project assignment.
                         */
                        .filter(
                          (user) =>
                            user.userType
                              ?.toLowerCase() ===
                            "user"
                        )
                        /*
                         * Don't show users who are
                         * already assigned.
                         */
                        .filter(
                          (user) =>
                            !projectUsers.some(
                              (projectUser) =>
                                projectUser.email
                                  ?.toLowerCase() ===
                                user.email
                                  ?.toLowerCase()
                            )
                        )
                        .map(
                          (
                            user,
                            index
                          ) => (
                            <option
                              key={
                                user.userNo ??
                                `${user.email}-${index}`
                              }
                              value={
                                user.email
                              }
                            >
                              {user.email}
                            </option>
                          )
                        )}
                    </select>

                    <button
                      onClick={
                        handleAssignUserToProject
                      }
                      className="w-full bg-purple-600 text-white py-2 rounded"
                    >
                      Assign
                    </button>

                  </div>
                )}

              </div>
            )
          )
        )}

      </div>

    </div>
  );
}